import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import locales from '../../../content.json';
import s from './index.module.scss';

import EveryDoggyAPIServices from '../../../utils/services/EveryDoggyAPIServices';
import Option from './option';
import PromoCode from '../../../components/common/promoCode';

import { setPaymentProduct } from '../../../reduxStore/data/actions';
import { getPricesConfig } from '../../getPricesConfig';
import { RootState } from '../../../store';
import { PAYMENT_TARIFF } from '../../../types';
import {
  EMPTY_OFFER_PRODUCTS,
  type OfferPriceConfig,
  type OfferProducts,
  formatOfferTitle,
  getCurrencySymbol,
  getDailyPrice,
  getFirstWeeklyPrice,
  getLabelDiscountText,
  getOldPeriodPrices,
  getOptionDiscountLabel,
  getPriceParts,
  getRecurringWeeklyPrice,
  hasMixPaywallDiscountPercent,
  isSpecialOfferTariff,
  normalizeDisplayedDayPrice,
  shouldShowRedLabelDiscount,
} from './helpers';

type OfferProps = {
  currentProduct: string;
  secondScreenWrapper: boolean;
};

type OfferOptionViewModel = {
  key: keyof OfferProducts;
  optionValue?: string;
  productName?: string;
  title: string;
  oldPeriodPrice: string | number;
  weeklyPrice: string | number;
  dailyPrice: string | number;
  dailyText: string;
  oldPricePayload?: string | number;
  showMostPopular?: boolean;
  showWeekSuffix?: boolean;
};

const Offer = ({ currentProduct, secondScreenWrapper }: OfferProps) => {
  const dispatch = useDispatch();
  const tariff = useSelector<RootState>((state) =>
    state.data.tariff) as PAYMENT_TARIFF;
  const lang = locales[window.userLang] ? window.userLang : 'en';
  const dict = locales[lang];
  const { extraOptions, everydoggyNewDesign } = useMemo(() => getPricesConfig(tariff), [tariff]);
  const [products, setProducts] = useState<OfferProducts>(EMPTY_OFFER_PRODUCTS);
  const showWeeklyText = tariff.includes(PAYMENT_TARIFF.V47);

  const {
    firstPriceConfig,
    secondPriceConfig,
    thirdPriceConfig,
  } = everydoggyNewDesign as {
    firstPriceConfig: OfferPriceConfig;
    secondPriceConfig: OfferPriceConfig;
    thirdPriceConfig: OfferPriceConfig;
  };

  const currency = getCurrencySymbol();
  const specialOffer = isSpecialOfferTariff(tariff);
  const showRedLabelDiscount = shouldShowRedLabelDiscount(tariff);
  const hasMixDiscount = hasMixPaywallDiscountPercent();
  const isMostPopularFirstOption = tariff === PAYMENT_TARIFF.V47;
  const labelDiscountText = getLabelDiscountText(tariff);
  const optionDiscountLabel = getOptionDiscountLabel(dict.promo_code_save, extraOptions?.discountPercent);

  useEffect(() => {
    let isActive = true;

    async function fetchData() {
      try {
        const [firstOption, secondOption, thirdOption] = await Promise.all([
          EveryDoggyAPIServices.getWebCatalogProduct(firstPriceConfig.id.toString()),
          EveryDoggyAPIServices.getWebCatalogProduct(secondPriceConfig.id.toString()),
          EveryDoggyAPIServices.getWebCatalogProduct(thirdPriceConfig.id.toString()),
        ]);

        if (isActive) {
          setProducts({ firstOption, secondOption, thirdOption });
        }
      } catch {
        if (isActive) {
          setProducts(EMPTY_OFFER_PRODUCTS);
        }
      }
    }

    fetchData();
    return () => {
      isActive = false;
    };
  }, [firstPriceConfig.id, secondPriceConfig.id, thirdPriceConfig.id]);

  const pricing = useMemo(() => {
    return {
      firstPrice: getFirstWeeklyPrice(products.firstOption, firstPriceConfig),
      secondPrice: getRecurringWeeklyPrice(products.secondOption, secondPriceConfig),
      thirdPrice: getRecurringWeeklyPrice(products.thirdOption, thirdPriceConfig),
      firstPriceInDay: getDailyPrice(products.firstOption, firstPriceConfig),
      secondPriceInDay: getDailyPrice(products.secondOption, secondPriceConfig),
      thirdPriceInDay: getDailyPrice(products.thirdOption, thirdPriceConfig),
    };
  }, [products, firstPriceConfig, secondPriceConfig, thirdPriceConfig]);

  const oldPeriodPrices = useMemo(() => {
    return getOldPeriodPrices({
      tariff,
      firstPriceConfig,
      secondPriceConfig,
      thirdPriceConfig,
      mixDiscountPercentEnabled: hasMixDiscount,
      redLabelDiscountEnabled: showRedLabelDiscount,
    });
  }, [firstPriceConfig, hasMixDiscount, secondPriceConfig, showRedLabelDiscount, tariff, thirdPriceConfig]);

  const options = useMemo<OfferOptionViewModel[]>(() => {
    return [
      {
        key: 'firstOption',
        optionValue: products.firstOption?.id,
        productName: firstPriceConfig.productName,
        title: formatOfferTitle(firstPriceConfig.titleWeek),
        oldPeriodPrice: oldPeriodPrices.first,
        weeklyPrice: pricing.firstPrice,
        dailyPrice: pricing.firstPriceInDay,
        dailyText: firstPriceConfig.perDay,
        oldPricePayload: firstPriceConfig.defaultOldPrices,
        showMostPopular: isMostPopularFirstOption,
        showWeekSuffix: showWeeklyText,
      },
      {
        key: 'secondOption',
        optionValue: products.secondOption?.id,
        productName: secondPriceConfig.productName,
        title: formatOfferTitle(secondPriceConfig.titleWeek),
        oldPeriodPrice: oldPeriodPrices.second,
        weeklyPrice: pricing.secondPrice,
        dailyPrice: pricing.secondPriceInDay,
        dailyText: firstPriceConfig.perDay,
        oldPricePayload: secondPriceConfig.defaultOldPrices,
        showMostPopular: !isMostPopularFirstOption,
        showWeekSuffix: true,
      },
      {
        key: 'thirdOption',
        optionValue: products.thirdOption?.id,
        productName: thirdPriceConfig.productName,
        title: formatOfferTitle(thirdPriceConfig.titleWeek),
        oldPeriodPrice: oldPeriodPrices.third,
        weeklyPrice: pricing.thirdPrice,
        dailyPrice: normalizeDisplayedDayPrice(pricing.thirdPriceInDay, true),
        dailyText: firstPriceConfig.perDay,
        oldPricePayload: thirdPriceConfig.defaultOldPrices,
        showWeekSuffix: true,
      },
    ];
  }, [
    firstPriceConfig,
    isMostPopularFirstOption,
    oldPeriodPrices.first,
    oldPeriodPrices.second,
    oldPeriodPrices.third,
    pricing.firstPrice,
    pricing.firstPriceInDay,
    pricing.secondPrice,
    pricing.secondPriceInDay,
    pricing.thirdPrice,
    pricing.thirdPriceInDay,
    products.firstOption?.id,
    products.secondOption?.id,
    products.thirdOption?.id,
    secondPriceConfig,
    thirdPriceConfig,
  ]);

  const handleSelect = (optionValue?: string, productName?: string, oldPrice?: string | number) => {
    if (!optionValue) {
      return;
    }

    dispatch(
      setPaymentProduct(
        JSON.stringify({
          product: optionValue,
          oldPrice,
          name: productName,
        })
      )
    );
  };

  return (
    <div
      className={`${s.optionsWrapper} ${specialOffer ? s.tariffV5 : ''}`}
    > 
      <h2 className={s.optionsTitle}>{dict.tariff_title_choose}</h2>
      <PromoCode tariff={tariff} secondScreenWrapper={secondScreenWrapper} />
      <div
        className={s.options}
      >
        {options.map(option => {
          const priceParts = getPriceParts(option.dailyPrice);

          return (
            <Option
              key={option.key}
              isSelected={currentProduct === option.optionValue}
              className={`${s.option} ${showRedLabelDiscount ? s.optionRedLabel : ''} ${
                option.showMostPopular ? s.optionMostPopular : ''
              }`}
              onSelect={() => handleSelect(option.optionValue, option.productName, option.oldPricePayload)}
            >
              {option.showMostPopular && (
                <div className={s.bestOffer}>{extraOptions?.mostPopularTitle}</div>
              )}

              {showRedLabelDiscount && (
                <p className={s.labelDiscount}>-{labelDiscountText}%</p>
              )}
              <div className={s.radio}></div>
              <div className={s.priceWrapper} style={currency === 'USD ' ? { paddingRight: '15px' } : undefined}>
                <p className={s.priceTitle}>{option.title}</p>
                <div className={s.priceMeta}>
                  <p className={s.price}>
                    <span className={s.lineRed}>
                      {currency}
                      {option.oldPeriodPrice}
                    </span>{' '}
                    {currency}
                    {option.weeklyPrice}
                    {option.showWeekSuffix ? ` ${dict.payment_text_56}` : ''}
                  </p>
                  <span className={s.optionDiscountBadge}>{optionDiscountLabel}</span>
                </div>
              </div>

              <div className={s.priceDay}>
                <div className={s.priceDayWrapper}>
                  <p className={s.dollar}>{currency}</p>
                  <div className={s.firstPart}>
                    {priceParts.whole}.{priceParts.cents}
                  </div>
                </div>
                <div className={s.priceDayText}>{option.dailyText}</div>
              </div>
            </Option>
          );
        })}
      </div>
    </div>
  );
};

export default Offer;
